import axios from 'axios';
import { createStandaloneToast } from '@chakra-ui/react';

const { toast } = createStandaloneToast();

const API = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Function to sanitize identifier (UUID or friendly name)
const sanitizeIdentifier = (identifier) => {
  const validIdentifierRegex = /^[a-zA-Z0-9_-]{3,}$/;
  return validIdentifierRegex.test(identifier) ? identifier : null;
};

// Function to get CSRF token from sessionStorage
const getCsrfToken = () => {
  const token = sessionStorage.getItem('csrfToken');
  console.log('getCsrfToken called, token:', token ? token.substring(0, 20) + '...' : 'NOT FOUND');
  return token || '';
};

export const setCsrfToken = (token) => {
  if (token) {
    sessionStorage.setItem('csrfToken', token);
    console.log('CSRF token stored in sessionStorage:', token.substring(0, 20) + '...');
  }
};

// Request interceptor to always include CSRF token
API.interceptors.request.use(
  (config) => {
    const token = getCsrfToken();
    
    console.log('=== REQUEST INTERCEPTOR ===');
    console.log('URL:', config.url);
    console.log('Method:', config.method);
    console.log('CSRF Token from storage:', token ? token.substring(0, 20) + '...' : 'MISSING');
    
    if (token) {
      config.headers['x-csrf-token'] = token;
      console.log('CSRF token added to headers');
    } else {
      console.error('WARNING: No CSRF token available for request!');
    }
    
    console.log('Final headers:', config.headers);
    console.log('===========================');
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle 401 and 403 errors
API.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      toast({
        title: 'Session expired.',
        description: "Let's get your information again.",
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
      
      // Get identifier from localStorage OR sessionStorage
      let identifier = localStorage.getItem('UUID') || sessionStorage.getItem('dentistUuid');
      
      // Clear session storage but preserve the identifier
      sessionStorage.clear();
      
      // Re-store the identifier in both storages for reliability
      if (identifier) {
        localStorage.setItem('UUID', identifier);
        sessionStorage.setItem('dentistUuid', identifier);
      }

      const sanitizedIdentifier = sanitizeIdentifier(identifier);
      
      if (sanitizedIdentifier) {
        // Valid identifier - redirect to login page
        console.log('Session expired - redirecting to login:', `/${sanitizedIdentifier}`);
        setTimeout(() => {
          window.location.href = `/${sanitizedIdentifier}`;
        }, 1000);
      } else {
        // No valid identifier - redirect to home page (NOT error page)
        console.warn('No valid identifier found - redirecting to home');
        sessionStorage.clear();
        localStorage.clear();
        setTimeout(() => {
          window.location.href = '/';
        }, 1000);
      }
    }

    // Handle CSRF token errors
    if (error.response && error.response.status === 403) {
      const errorMessage = error.response.data?.message || error.response.data?.error || '';
      if (errorMessage.toLowerCase().includes('csrf')) {
        toast({
          title: 'Security token expired.',
          description: "Please refresh the page and try again.",
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        });
        
        // Clear CSRF token and force refresh
        sessionStorage.removeItem('csrfToken');
        console.error('CSRF token validation failed');
      }
    }

    return Promise.reject(error);
  }
);

// Generate temporary token with identifier (UUID or friendly name)
export const generateTempToken = (identifier) => {
  const sanitizedIdentifier = sanitizeIdentifier(identifier);
  
  console.log('=== GENERATE TEMP TOKEN ===');
  console.log('Identifier:', sanitizedIdentifier);
  
  if (sanitizedIdentifier) {
    // The interceptor will automatically add the CSRF token
    return API.post('/web/auth/generate-temp-token', 
      { webAppUUID: sanitizedIdentifier }
    );
  } else {
    return Promise.reject(new Error('Invalid identifier format'));
  }
};

export default API;